import {
  CommitRounded as CommitRoundedIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  OpenInNewRounded as OpenInNewRoundedIcon,
} from '@mui/icons-material';
import { Box, Grid, IconButton, LinearProgress, Link, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as send from '../../../../../../lib/analytics/send';
import getPlotData from '../../../../../../lib/explorer/_getPlotData';
import { needPremium } from '../../../../../needPremium';
import { parseTabData } from '../../../../../utils/helpers';
import Chart from '../../../../_common/VisxChart';
import HistoricalDataTable from './HistDataTable';
import PopDropdown from './PopDropdown';
import SeriesSearch from './PopSeriesSearch';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(10, 25, 41)' : '#fff',
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow:
    theme.palette.mode === 'dark'
      ? 0
      : '0 0.5px 1px rgb(255 255 255 / 20%), 0 2px 3px rgb(255 255 255 / 10%)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgb(19, 47, 76)'
      : '1px solid #eae9ee',
  borderRadius: '7px',
  '& .Item-Head': {
    background: '#fbfbfc',
    borderBottom: '1px solid #eae9ee',
    marginBottom: 0,
    padding: theme.spacing(1, 2),
    borderRadius: '7px 7px 0 0',
    '& a': { textDecoration: 'none', color: '#333', fontWeight: 600 },
    '& .option-title': { fontWeight: 600, color: '#13113c', fontSize: '.9rem' },
  },
  '& .Item-Body': { padding: theme.spacing(1) },
  '& .skeleton:after': {
    animation: '2s linear 0.5s infinite normal none running animation-wiooy9',
    background: 'linear-gradient(90deg, transparent, rgb(0 0 0 / 9%), transparent)',
    content: "''",
    position: 'absolute',
    transform: 'translateX(-100%)',
    inset: '0px',
  },
}));

const COMPACT_CHART_SETTING = {
  themeName: 'Default',
  aspRatio: 2,
  chartMargin: { bottom: 30, right: 50 },
  xaxis: { setting: { label: { fs: '30px' } } },
  yaxis: { setting: { label: { fs: '30px' } } },
  grid: { line: { stroke: '#999999', dashArray: '1,1' } },
};

const PLACEHOLDER_RE = /XXXXX/g;

const noop = () => { };

const useChartConfig = (initialTicker) => {
  const safeTicker = initialTicker?.toUpperCase() ?? '';

  const [chartConfig, setChartConfig] = useState(() => ({
    ticker: safeTicker,
    seriesCode: safeTicker ? `${safeTicker}.REV.TTM` : 'XXXXX.REV.TTM',
    seriesName: 'Revenues (TTM)',
    legend: 'Net sales',
    code: 'REV.TTM',
  }));

  useEffect(() => {
    if (!initialTicker) return;
    const upper = initialTicker.toUpperCase();
    setChartConfig((prev) => ({
      ...prev,
      ticker: upper,
      seriesCode: `${upper}.REV.TTM`,
    }));
  }, [initialTicker]);

  const updateChartConfig = useCallback((updates) => {
    setChartConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  return { chartConfig, updateChartConfig };
};

const useHistoricalChartData = (ticker, code) => {
  const [newMetricData, setNewMetricData] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchIdRef = useRef(0);

  const getHistChartData = useCallback(async (fetchTicker, fetchCode) => {
    if (!fetchTicker || !fetchCode) return;
    const fetchId = ++fetchIdRef.current;
    setLoading(true);

    const convertedCode = `${fetchTicker}_` + fetchCode.replace(/[\.\@\|\$\^\:]/g, '_');
    try {
      const data = await getPlotData(`${fetchTicker}.${fetchCode}`);
      if (fetchId !== fetchIdRef.current) return;
      const metricArray = data?.[convertedCode] ?? [];
      const sortedData = [...metricArray].sort(
        (a, b) => new Date(b.recordDate) - new Date(a.recordDate)
      );
      setNewMetricData(sortedData.length > 0 ? sortedData : null);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('Error fetching historical chart data:', error);
      setNewMetricData(null);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ticker || !code) return;
    getHistChartData(ticker, code);
    return () => {
      fetchIdRef.current += 1;
    };
  }, [ticker, code, getHistChartData]);

  return { newMetricData, loading, getHistChartData };
};

const HistoricalCharts = ({
  tabData,
  info,
  divideColumnsBy,
  isPremium,
  fullWidth,
  showPricingBanner,
  desk,
}) => {
  const { ticker } = info || {};
  const { chartConfig, updateChartConfig } = useChartConfig(ticker);

  const { newMetricData, loading, getHistChartData } = useHistoricalChartData(
    chartConfig.ticker,
    chartConfig.code
  );

  const metricsChartsData = useMemo(() => {
    const data = tabData?.find((d) => d.section_code === 'historical_data');
    return data ? parseTabData(data) : null;
  }, [tabData]);

  const metricChartItem = useMemo(() => {
    const data = tabData?.find((d) => d.section_code === 'historical_chart');
    return data ? parseTabData(data) : null;
  }, [tabData]);

  const chartItem = useMemo(() => metricChartItem?.tabData ?? null, [metricChartItem]);
  const chartSeriesData = useMemo(() => chartItem?.series ?? [], [chartItem]);

  const resolvedSeriesCode = useMemo(
    () => chartConfig.seriesCode.replace(PLACEHOLDER_RE, chartConfig.ticker),
    [chartConfig.seriesCode, chartConfig.ticker]
  );

  const chartData = useMemo(() => {
    const rawData = newMetricData ?? metricsChartsData?.tabData;
    if (!rawData) return null;

    const transformedSeries = chartSeriesData.map((d) => ({
      ...d,
      yformula: resolvedSeriesCode,
    }));

    const transformedSeriesData = rawData
      .filter((item) => item.value != null && item.recordDate != null)
      .map((item) => ({
        date: new Date(item.recordDate),
        value: item.value,
      }));

    return {
      ...chartItem,
      id: resolvedSeriesCode,
      chartSetting: fullWidth
        ? { ...chartItem?.chartSetting, aspRatio: 3 }
        : COMPACT_CHART_SETTING,
      series: transformedSeries,
      xSeries: chartItem?.x_series,
      ySeries: resolvedSeriesCode,
      fromDate: chartItem?.recordDate ?? '',
      toDate: '',
      series_data: transformedSeriesData,
      stock_data: [],
    };
  }, [
    newMetricData,
    metricsChartsData,
    chartSeriesData,
    resolvedSeriesCode,
    chartItem,
    fullWidth,
  ]);

  const handleDownload = useCallback(
    (e) => {
      if (isPremium) {
        const jsonData = JSON.stringify(
          newMetricData ?? metricsChartsData?.tabData,
          null,
          2
        );
        const fileBlob = new Blob([jsonData], { type: 'application/json' });
        const downloadUrl = URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${chartConfig.ticker} ${chartConfig.seriesName}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
        send.uevent(e);
      } else {
        showPricingBanner(e, 'hist');
      }
    },
    [isPremium, newMetricData, metricsChartsData, chartConfig, showPricingBanner]
  );

  const setSeries = useCallback(
    (config) => {
      const newCode = config?.seriesCode?.replace(`${ticker}.`, '') ?? '';
      updateChartConfig({ ...config, ticker });
      getHistChartData(ticker, newCode);
    },
    [updateChartConfig, getHistChartData, ticker]
  );

  if (!chartData) return null;

  const activeData = newMetricData ?? metricsChartsData?.tabData;
  const sectionTitle =
    metricsChartsData?.section_name?.replace(PLACEHOLDER_RE, chartConfig.ticker) ?? '';

  return (
    <Grid size={{ xs: 12, sm: 12 }}>
      <Item className="crisischart">
        <Box
          className="Item-Head"
          sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 0.5, px: 0.5 }}
        >
          <Box
            component="div"
            sx={{
              '& .api-link': { color: '#174c86 !important', fontWeight: '500 !important' },
              '& .api-link:hover': { textDecoration: 'underline !important' },
            }}
          >
            <Box component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography component="p" className="option-title" sx={{ mr: 2, whiteSpace: 'nowrap' }}>
                {sectionTitle}
              </Typography>
            </Box>
            <Box
              component="div"
              sx={{ mt: 0, fontSize: '.85rem', display: { md: 'block', sm: 'none', xs: 'none' } }}
            >
              Get all data in R, Python etc through our&nbsp;
              <Link
                className="api-link"
                target="_blank"
                rel="noopener noreferrer"
                href="/docs/api?lang=r#historical-metrics-data"
                underline="hover"
              >
                Historical Stock Data APIs
                <OpenInNewRoundedIcon
                  sx={{ fontSize: '.9rem', verticalAlign: 'middle' }}
                  aria-hidden="true"
                />
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                  }}
                >
                  (opens in new tab)
                </Box>
              </Link>
            </Box>
          </Box>
          <Box
            component="div"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              bgcolor: '#fbfbfc',
              m: { md: '0 0 0 auto', xs: '7px auto 0 0' },
              borderRadius: '8px',
            }}
          >
            <SeriesSearch
              setShowSearch={noop}
              placeholder={`Search among hundreds of data series for ${chartConfig.ticker}...`}
              ticker={chartConfig.ticker}
              setLoading={noop}
              setSeries={setSeries}
            />
            <IconButton
              size="small"
              aria-label={`Download ${chartConfig.seriesName} data as JSON`}
              data-event-name="download-csv"
              data-event-category="stock"
              data-event-label={
                sectionTitle
                  ? `${sectionTitle}${isPremium ? '' : ' (Banner)'}`
                  : 'Historical Charts'
              }
              sx={{
                color: '#565b75',
                background: '#fbfbfc !important',
                border: '1px solid #dbdce4',
                textTransform: 'none',
                ml: 2,
                '&:hover': { background: '#f2f2f2 !important' },
              }}
              onClick={handleDownload}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, my: 0 }}>
          <Grid container spacing={1.2}>
            <Grid size={{ xs: 12, sm: 12, md: divideColumnsBy || 12 }}>
              <Box
                component="div"
                className="option-content"
                sx={{
                  minHeight: { md: '17.9rem', sm: '15rem', xs: '11rem' },
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {loading && (
                  <>
                    <LinearProgress aria-label="Loading chart data" sx={{ inset: 0, zIndex: 7, position: 'absolute', display: 'block', width: '100%' }} />
                    <span
                      role="status"
                      aria-busy="true"
                      aria-label="Loading chart data"
                      className="skeleton"
                      style={{
                        display: 'block',
                        backgroundColor: 'rgba(0, 0, 0, 0.11)',
                        position: 'absolute',
                        inset: 0,
                        zIndex: 2,
                        maskImage: '-webkit-radial-gradient(center, white, black)',
                        overflow: 'hidden',
                      }}
                    />
                  </>
                )}
                <Grid container spacing={1} direction={{ sm: 'row-reverse' }}>
                  <Grid size={{ xs: 12, sm: 12, md: fullWidth ? 8.5 : 7.5 }} sx={{ mt: 0.7 }}>
                    <Box sx={{ textAlign: 'right', display: 'flex' }}>
                      <PopDropdown
                        ticker={chartConfig.ticker}
                        seriesName={chartConfig.seriesName}
                        setLoading={noop}
                        setSeries={setSeries}
                        loading={loading}
                      />
                    </Box>
                    <Box className="wide-chartt">
                      <Box
                        component="div"
                        className="option-content Item-Body"
                        sx={{ mb: 1, pb: '0 !important' }}
                      >
                        <Chart
                          data={chartData}
                          _height={desk ? 300 : 200}
                          _margin={{ top: 10, right: 50, bottom: 20, left: 20 }}
                        />
                      </Box>
                      <Box
                        component="div"
                        sx={{
                          borderRadius: '4px',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        {activeData &&
                          chartItem?.series?.map((d, i) => (
                            <Box
                              key={d?.legend?.label || i}
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mt: 0.4,
                                mr: 1.5,
                                mb: 1,
                              }}
                            >
                              <CommitRoundedIcon
                                sx={{
                                  mr: 0.7,
                                  width: '1em',
                                  fontSize: '1.2rem !important',
                                  color: d?.setting?.line?.stroke,
                                }}
                              />
                              <Typography variant="body2" sx={{ fontSize: '.75rem', fontWeight: 600 }}>
                                {d?.legend?.label || chartConfig.legend || chartConfig.seriesName}
                              </Typography>
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 12, md: fullWidth ? 3.5 : 4.5 }}>
                    <HistoricalDataTable historicaldata={activeData} />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box
          component="div"
          sx={{
            py: 1,
            px: 2,
            fontSize: '.8rem',
            display: { md: 'none', sm: 'block', xs: 'block' },
            borderTop: '1px solid #eae9ee',
            background: '#f7f8fa',
          }}
        >
          Get all data in R, Python etc through our&nbsp;
          <Link
            className="api-link"
            target="_blank"
            rel="noopener noreferrer"
            href="/docs/api?lang=r#historical-metrics-data"
            underline="hover"
          >
            Historical Stock Data APIs
            <OpenInNewRoundedIcon
              sx={{ fontSize: '.8rem', verticalAlign: 'middle' }}
              aria-hidden="true"
            />
            <Box
              component="span"
              sx={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
              }}
            >
              (opens in new tab)
            </Box>
          </Link>
        </Box>
      </Item>
    </Grid>
  );
};

export default React.memo(needPremium(HistoricalCharts));